import { makeAutoObservable, runInAction, configure } from 'mobx';
import { createWishListAndAddItem } from '../helpers/wishList/CreateWishListAndAddItem';
import { addItemToWishList } from '../helpers/wishList/AddItemToWishList/AddItemToWishList';
import { getAndMapWishLists } from '../helpers/wishList/GetAndMapWishLists';
import {
    getSkuId,
    getSkuSelected,
    getProductMetadata,
    getProductDetails,
} from '../helpers/productDetails/productDetails';
import {
    GENERIC_ERROR_MESSAGE,
    LIST_FULL_ERROR_MESSAGE,
    SELECT_SIZE_ERROR_MESSAGE,
    WISH_LIST_QUERY_STRING,
} from './constants';
import {
    isItemInWishList,
    updateProductWishList,
    mapProductWishLists,
    removeQueryParamFromURL,
    logAPIErrorToNewrelic,
} from '../helpers/wishList/utils';
import { deleteItemFromWishList } from '../helpers/wishList/DeleteItemFromWishList/DeleteItemFromWishList';
import EmailModalStore from './EmailModalStore/EmailModalStore';
import { APIResult } from '../helpers/wishList/APIResult';
import { FEATURE_FLAGS } from '../helpers/featureFlags/constants';
import { isFlagSet } from '../helpers/featureFlags/featureFlags';
import { shouldCollapseWishLists } from '../helpers/monetate/monetate';
import { log } from '../utils/logger';

// Add support for browsers without Proxy object support
configure({ useProxies: 'ifavailable' });

class WishListsStore {
    constructor(emailModalStore?: EmailModalStore) {
        makeAutoObservable(this, {}, { autoBind: true });
        this.emailModalStore = emailModalStore || undefined;
    }

    wishLists: ProductWishList[] = [];
    currentProductSkuId = '';
    currentProductId = '';
    currentAvailableProductSkus: number | undefined = undefined;
    isSkuIdSelected = false;
    errorMessage = '';
    hasUserInteracted = false;
    errorRetrievingWishlists = false;
    isCallingAPI = false;
    currentProductMetadata = '';
    emailModalStore: EmailModalStore | undefined = undefined;
    collapsedByDefault: boolean | undefined = undefined;
    collapsed = true;

    get wishListsLength(): number {
        return this.wishLists.length;
    }

    get hasOneOrMoreWishlist(): boolean {
        return this.wishLists.length >= 1;
    }

    get hasMoreThanOneWishlist(): boolean {
        return this.wishLists.length > 1;
    }

    get hasError(): boolean {
        return this.errorMessage.length > 0;
    }

    get productExistsInWishLists(): boolean {
        return this.wishLists.some((wishList) => {
            return wishList.isAdded;
        });
    }

    get shouldWishListsBeExpanded(): boolean {
        // we always have the slight expand if we need to show an error or if the item is in the list
        if (this.hasError || this.productExistsInWishLists) {
            return true;
        }

        // if we have opt-ed into the collapse test, and the default is collapsed then we collapse it
        if (this.collapsed) {
            return false;
        }

        // we expand if they have more than 1 list, and the item is not in the lists and is not in the collapse test
        return this.hasMoreThanOneWishlist;
    }

    handleEmailMeModal(): void {
        const productDetails = this.parseProductDetails(getProductDetails());

        if (productDetails?.isOutOfStock) {
            this.emailModalStore?.openEmailModal({
                skuId: this.currentProductSkuId,
                productId: productDetails.productId,
            });
        }
    }

    checkSkuSelected(): void {
        this.isSkuIdSelected = getSkuSelected();
        if (this.hasUserInteracted && !this.isSkuIdSelected) {
            this.errorMessage = SELECT_SIZE_ERROR_MESSAGE;
        }
    }

    checkIfWishListInURL(): void {
        const queryParams = window.location.search;
        // this needs the undefined check for the tests which do not return search
        if (queryParams && queryParams.includes(WISH_LIST_QUERY_STRING)) {
            removeQueryParamFromURL(window.location.href, WISH_LIST_QUERY_STRING);
            this.hasUserInteracted = true;
            !this.hasMoreThanOneWishlist && this.handleWishListOnClick('');
        }
    }

    setupEventListeners(): void {
        document.body.addEventListener('displayedProduct', ((event: CustomEvent) => {
            runInAction(() => {
                this.errorMessage = '';
                this.currentProductSkuId = event.detail.skuId;
                if (event.detail.productMetadata) {
                    this.currentProductMetadata = JSON.stringify(event.detail.productMetadata);
                }
            });
        }) as EventListener);
    }

    mapProductWishListForCurrentSku(): void {
        if (this.wishLists) {
            const productMetadata = this.parseCurrentProductMetadata();
            runInAction(
                () => (this.wishLists = mapProductWishLists(this.wishLists, this.currentProductSkuId, productMetadata)),
            );
        }
    }

    handleWishListOnClick = async (wishListId: string): Promise<void> => {
        if (!wishListId && this.collapsedByDefault && this.collapsed) {
            runInAction(() => {
                this.collapsed = false;
            });
        }

        if (!wishListId && this.hasMoreThanOneWishlist) {
            // do nothing when multiple lists exist and a user clicks the top button
            return;
        }

        !this.hasUserInteracted &&
            runInAction(() => {
                this.hasUserInteracted = true;
            });

        const { currentProductSkuId, errorRetrievingWishlists } = this;

        if (errorRetrievingWishlists) {
            await this.getWishlists(false);
        }

        this.checkSkuSelected();
        if (!this.isSkuIdSelected) {
            runInAction(() => {
                this.errorMessage = SELECT_SIZE_ERROR_MESSAGE;
            });
            return;
        }

        runInAction(() => {
            // no pre-conditions stopping the add, reset and try adding product
            this.errorMessage = '';
        });

        if (!this.wishLists || this.wishListsLength == 0) {
            const productMetadata = this.parseCurrentProductMetadata();

            const itemDetails = this.constructItemDetails(currentProductSkuId);
            const newWishLists = await this.handledAPIResult(() =>
                createWishListAndAddItem(currentProductSkuId, itemDetails, productMetadata),
            );

            if (newWishLists) {
                runInAction(() => (this.wishLists = newWishLists));
            }

            return;
        }

        if (!wishListId) {
            wishListId = this.wishLists[0].id;
        }

        const productExistsInWishlist = isItemInWishList(this.wishLists, wishListId);
        if (productExistsInWishlist) {
            await this.removeItemFromWishList(wishListId, currentProductSkuId);
        } else {
            await this.addToWishList(wishListId);
        }
    };

    handledAPIResult = async <T>(getData: () => Promise<APIResult<T>>): Promise<T | null> => {
        let showError = false;
        let errorMessage = GENERIC_ERROR_MESSAGE;

        try {
            runInAction(() => {
                this.isCallingAPI = true;
            });

            const apiResult = await getData();
            if (apiResult.success) {
                if (apiResult.data == null) {
                    logAPIErrorToNewrelic(`handledAPIResult`, `getData | Unexpected data scenario | No Data`, {
                        ...apiResult,
                        response: apiResult,
                        errorThrown: true,
                    });
                    throw Error('Unexpected data scenario');
                }

                return apiResult.data;
            } else {
                if (apiResult.showError) {
                    showError = true;
                    if (apiResult.errorMessage) {
                        errorMessage = apiResult.errorMessage;
                    }
                    logAPIErrorToNewrelic(`handledAPIResult`, `getData | Response not OK`, {
                        ...apiResult,
                        response: apiResult,
                    });
                }
            }
        } catch (error) {
            showError = true;
            logAPIErrorToNewrelic(`handledAPIResult`, `getData | Exception`, {
                error: error,
                showError: showError,
            });
        } finally {
            runInAction(() => {
                this.isCallingAPI = false;
            });
        }

        if (showError) {
            runInAction(() => (this.errorMessage = errorMessage));
        }

        return null;
    };

    // API Calls
    getWishlists = async (allowedToFail = true): Promise<void> => {
        runInAction(() => {
            this.currentProductSkuId = this.currentProductSkuId || getSkuId();
            this.currentProductMetadata = this.currentProductMetadata || getProductMetadata();
        });

        const wishLists = await this.handledAPIResult(() => getAndMapWishLists(allowedToFail));
        if (wishLists) {
            runInAction(() => {
                this.wishLists = wishLists;
                this.errorRetrievingWishlists = false;
            });

            this.setCollapsedByDefault();
        } else {
            this.errorRetrievingWishlists = true;
            logAPIErrorToNewrelic(`getWishlists`, `getAndMapWishLists | errorRetrievingWishlists`, {
                response: wishLists,
            });
        }
    };

    addToWishList = async (wishListId?: string): Promise<void> => {
        const { wishLists, wishListsLength, currentProductSkuId, productExistsInWishLists } = this;

        if (!wishLists || wishListsLength == 0) {
            logAPIErrorToNewrelic(`addToWishList`, `no wishlist found!`, {
                wishListId: wishListId,
                currentProductSkuId: currentProductSkuId,
                productExistsInWishLists: productExistsInWishLists,
            });
            return;
        }

        const productMetadata = this.parseCurrentProductMetadata();

        let wishListIdToUse = wishLists[0].id; // default
        if (wishListsLength == 1) {
            if (productExistsInWishLists) {
                logAPIErrorToNewrelic(`addToWishList`, `Product already in List`, {
                    wishListIdToUse: wishListIdToUse,
                    currentProductSkuId: currentProductSkuId,
                    productMetadata: productMetadata,
                });
                return;
            }

            if (wishLists[0].isFull) {
                runInAction(() => {
                    this.errorMessage = LIST_FULL_ERROR_MESSAGE;
                });

                logAPIErrorToNewrelic(`addToWishList`, `List Full`, {
                    wishListIdToUse: wishListIdToUse,
                    currentProductSkuId: currentProductSkuId,
                    productMetadata: productMetadata,
                    errorMessage: this.errorMessage,
                });
                return;
            }
        }

        if (wishListsLength > 1 && wishListId) {
            wishListIdToUse = wishListId;
        }
        const itemDetails = this.constructItemDetails(currentProductSkuId);

        const updatedWishList = await this.handledAPIResult(() =>
            addItemToWishList(wishListIdToUse, currentProductSkuId, itemDetails, productMetadata),
        );

        if (updatedWishList) {
            runInAction(() => {
                this.wishLists = updateProductWishList(this.wishLists, {
                    ...updatedWishList,
                    isAdded: true,
                    isFull: false,
                });
                this.handleEmailMeModal();
            });
        }
    };

    constructItemDetails(currentSkuId: string): object {
        return {
            currentSkuId: currentSkuId,
            currentProductId: this.currentProductId,
            currentAvailableProductSkus: this.currentAvailableProductSkus,
            isSkuIdSelected: this.isSkuIdSelected,
            errorMessage: this.errorMessage,
            hasUserInteracted: this.hasUserInteracted,
            errorRetrievingWishlists: this.errorRetrievingWishlists,
            currentProductMetadata: this.currentProductMetadata,
        };
    }

    removeItemFromWishList = async (wishListId: string, skuId: string): Promise<void> => {
        const productMetadata = this.parseCurrentProductMetadata();

        const updatedWishList = await this.handledAPIResult(() =>
            deleteItemFromWishList(wishListId, skuId, productMetadata),
        );

        if (updatedWishList) {
            const productWishList: ProductWishList = { ...updatedWishList, isAdded: false, isFull: false };
            const newUpdatedWishLists = updateProductWishList(this.wishLists, productWishList);
            runInAction(() => {
                this.wishLists = newUpdatedWishLists;
            });
        }
    };

    parseProductDetails = (json: string): ProductDetails | undefined => {
        try {
            if (json) {
                return JSON.parse(json) as ProductDetails;
            }
        } catch {
            // invalid product metadata
            logAPIErrorToNewrelic(`parseProductDetails`, `Invalid product metadata`, {
                json: json,
            });
        }

        return undefined;
    };

    parseCurrentProductMetadata = (): ProductMetadata | undefined => {
        try {
            if (this.currentProductMetadata) {
                return JSON.parse(this.currentProductMetadata) as ProductMetadata;
            }
        } catch {
            // invalid product metadata
            logAPIErrorToNewrelic(`parseCurrentProductMetadata`, `Invalid product metadata`, {
                currentProductMetadata: this.currentProductMetadata,
            });
        }

        return undefined;
    };

    setCollapsedByDefault = async (): Promise<void> => {
        if (this.wishListsLength > 1 && this.collapsedByDefault === undefined) {
            const shouldCollapse = await shouldCollapseWishLists();
            log('Collapsing wish lists:', shouldCollapse);

            runInAction(() => {
                this.collapsedByDefault = shouldCollapse || isFlagSet(FEATURE_FLAGS.COLLAPSED_BUTTON);
                this.collapsed = this.collapsedByDefault;
            });
        }
    };
}

export default WishListsStore;
